import { Button } from "primereact/button";
import { OverlayPanel } from "primereact/overlaypanel";
import { useContext, useRef, useState } from "react";
import * as client from "../../client";
import { featureEnabled } from "../../client-features";
import { useClientId } from "../../ClientConfig";
import { ThemeContext } from "../../themes/Theme";
import styles from "./CRMContact.module.css";
import { EditCRMContactModal } from "./EditCRMContact";
import { getContactLabel, getOrganizationLabel } from "./orgLabels";

type CRMContactProps = {
  size?: "small" | "large" | "full_width";
  contact: client.ReportContact | null;
  // Fallback name to show if contact not found
  brokerageName: string | null | undefined;
  reportId: string;
};

export const CRMContact: React.FC<CRMContactProps> = ({
  contact,
  brokerageName,
  reportId,
  size = "small",
}) => {
  const op = useRef<OverlayPanel | null>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);

  const organizationLabel = getOrganizationLabel(contact);
  const contactTypeLabel = getContactLabel(contact);
  const clientId = useClientId();

  const brokeragePreferred = featureEnabled(
    clientId,
    "crm-brokerage-preferred"
  );
  // Determine display name and create placeholder contact if needed
  const computedBrokerageName = contact?.organization_name || brokerageName;
  const displayName = brokeragePreferred
    ? computedBrokerageName || "None"
    : contact?.full_name || computedBrokerageName || "None";

  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";
  const icon_name =
    brokeragePreferred && computedBrokerageName
      ? "pi-building"
      : contact && contact.full_name
        ? "pi-user"
        : contact && contact.organization_name
          ? "pi-building"
          : "pi-question-circle";
  return (
    <>
      <div
        className={styles.container}
        style={{
          backgroundColor: isLightMode ? "var(--blue-100)" : "var(--blue-900)",
          width:
            size === "small" ? "132px" : size === "large" ? "170px" : "100%",
          height: size === "small" ? "24px" : "32px",
        }}
        tabIndex={0}
        role="button"
        onClick={(e) => {
          e.stopPropagation();
          if (contact) {
            op.current?.toggle(e);
          } else {
            setEditModalVisible(true);
          }
        }}
      >
        <>
          <i className={`pi ${icon_name}`} style={{ fontSize: "12px" }}></i>
          <span className={styles.contactName}>{displayName}</span>
        </>
      </div>
      {contact && (
        <OverlayPanel
          ref={op}
          onClick={(e) => e.stopPropagation()}
          dismissable={true}
          showCloseIcon={true}
        >
          <div className={styles.verticalContainer}>
            <div className={styles.header}>
              <div className={styles.name}>{contact.full_name}</div>
              <div className={styles.meta}>{contact.email}</div>
            </div>

            <div className={styles.infoRow}>
              <div className={styles.label}>{organizationLabel}:</div>
              <div className={styles.value}>
                {contact.organization_name || "N/A"}
              </div>
            </div>

            {contact.office_phone && (
              <div className={styles.infoRow}>
                <div className={styles.label}>Office:</div>
                <div className={styles.value}>{contact.office_phone}</div>
              </div>
            )}

            {contact.cell_phone && (
              <div className={styles.infoRow}>
                <div className={styles.label}>Cell:</div>
                <div className={styles.value}>{contact.cell_phone}</div>
              </div>
            )}

            {contact.role && (
              <div className={styles.infoRow}>
                <div className={styles.label}>Role:</div>
                <div className={styles.value}>{contact.role}</div>
              </div>
            )}

            <div className={styles.actionRow}>
              <Button
                className={styles.editButton}
                size="small"
                label={`Edit ${contactTypeLabel}`}
                onClick={() => setEditModalVisible(true)}
              />
            </div>
          </div>
        </OverlayPanel>
      )}
      <EditCRMContactModal
        visible={editModalVisible}
        onHide={() => setEditModalVisible(false)}
        contact={
          contact || {
            id: "",
            email: "",
            full_name: "",
            organization_name: computedBrokerageName || "",
            contact_type: "broker",
            role: "",
            office_phone: "",
            cell_phone: "",
          }
        }
        reportId={reportId}
      />
    </>
  );
};
